import { useLayoutContext } from "@/components/Layout";
import { useSavingsBreakdown } from "@/api/hooks/use-billing";
import { useUsageSavings } from "@/api/hooks/use-usage";
import { KpiCard } from "@/components/KpiCard";
import { KpiGrid } from "@/components/KpiGrid";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { formatUsd } from "@/lib/format";

const OPTIMIZATION_LABELS: Record<string, string> = {
  routing: "Model Routing",
  cache: "Semantic Cache",
  pruning: "Message Pruning",
  cleanup: "Prompt Cleanup",
  compression: "Compression",
  provider_cache: "Provider Cache",
  l1: "Tool Output Trimming",
};

const OPTIMIZATION_COLORS: Record<string, string> = {
  routing: "bg-electric-blue",
  cache: "bg-safe-green",
  pruning: "bg-caution-amber",
  cleanup: "bg-purple-500",
  compression: "bg-cyan-500",
  provider_cache: "bg-indigo-500",
  l1: "bg-teal-500",
};

export function SavingsBreakdown() {
  const { tenantId, range, userId } = useLayoutContext();
  const breakdownQ = useSavingsBreakdown(tenantId, range, userId);
  const savingsQ = useUsageSavings(tenantId, range, userId);

  if (!tenantId) {
    return <EmptyState message="Enter a Tenant ID in the sidebar to get started" />;
  }

  if (breakdownQ.isLoading || savingsQ.isLoading) {
    return <LoadingSkeleton cards={3} rows={6} />;
  }

  if (breakdownQ.isError || savingsQ.isError) {
    return (
      <ErrorState
        message={breakdownQ.error?.message || savingsQ.error?.message || "Failed to load savings"}
        onRetry={() => { breakdownQ.refetch(); savingsQ.refetch(); }}
      />
    );
  }

  const breakdown = breakdownQ.data ?? {};
  const savings = savingsQ.data!;
  const totalSaved = parseFloat(savings.cost_saved);

  const entries = Object.entries(breakdown)
    .map(([key, value]) => ({
      key,
      label: OPTIMIZATION_LABELS[key] || key,
      amount: parseFloat(value),
      color: OPTIMIZATION_COLORS[key] || "bg-fg-muted",
    }))
    .filter((e) => Math.abs(e.amount) >= 0.005) // a $0.00 row says nothing
    .sort((a, b) => b.amount - a.amount);

  const grandTotal = entries.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold text-fg">Savings Breakdown</h1>

      <KpiGrid>
        <KpiCard
          label="Verified Savings"
          value={formatUsd(savings.cost_saved)}
          color="green"
        />
        <KpiCard
          label="Billable Requests"
          value={String(savings.count)}
        />
        <KpiCard
          label="Avg Savings / Request"
          value={savings.count > 0 ? formatUsd(String(totalSaved / savings.count)) : "$0.00"}
        />
      </KpiGrid>

      {/* Breakdown table */}
      <div className="rounded-lg border border-line-subtle bg-surface">
        <div className="px-4 py-3 border-b border-line-subtle">
          <h2 className="text-sm font-medium text-fg">By Optimization Type</h2>
        </div>
        {entries.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-fg-muted">
            No savings data for this period
          </div>
        ) : (
          <div className="divide-y divide-line-subtle">
            {entries.map(({ key, label, amount, color }) => {
              const pct = grandTotal > 0 ? (amount / grandTotal) * 100 : 0;
              return (
                <div key={key} className="flex items-center gap-4 px-4 py-3">
                  <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
                  <span className="text-sm text-fg flex-1">{label}</span>
                  <span className="text-sm font-mono text-safe-green">
                    {formatUsd(String(amount))}
                  </span>
                  <span className="text-xs text-fg-muted w-16 text-right">
                    {pct.toFixed(1)}%
                  </span>
                  <div className="w-32 h-1.5 rounded-full bg-surface-3 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${color}`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
